import React, { useState, useEffect } from "react";
import { useApolloClient } from "@apollo/client";
import { Table } from "antd";
import {
  IconChevronDown,
  IconBuildingBank,
  IconCreditCardFilled,
  IconCheck,
} from "@tabler/icons-react";
import dayjs from "dayjs";
import { portalPayments } from "_graphql/queries/portal";
import "./PaymentSchedulePanel.scss";

// Mobile breakpoint in pixels (matches $breakpoint-large from variables)
const MOBILE_BREAKPOINT = 1024;

function PaymentSchedulePanel({ applicationId, bankAccount }) {
  const client = useApolloClient();
  const [loading, setLoading] = useState(true);
  const [payments, setPayments] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" && window.innerWidth <= MOBILE_BREAKPOINT
  );

  // Listen for window resize to update mobile state
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fetchPayments = async () => {
    if (!applicationId) return;

    try {
      const queryData = await client.query({
        variables: { id: applicationId },
        query: portalPayments,
      });

      setPayments(queryData?.data?.portalPayments?.data || []);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayments();
  }, [applicationId]);

  const getStatusDisplay = (payment) => {
    // Handle actual payment statuses first
    if (payment.status === "paid" || payment.status === "success") {
      return { text: "Paid", type: "paid" };
    }

    if (payment.status === "failed") {
      return { text: "Failed", type: "failed" };
    }

    if (payment.status === "return") {
      return { text: "Returned", type: "failed" };
    }

    if (payment.status === "processing") {
      return { text: "Processing", type: "processing" };
    }

    if (payment.status === "cancelled") {
      return { text: "Cancelled", type: "cancelled" };
    }

    // For "open" status, calculate due date info
    const dueDate = dayjs(parseInt(payment.next_attempt));
    const today = dayjs().startOf("day");
    const daysUntil = dueDate.diff(today, "day");

    if (daysUntil < 0) {
      return { text: "Overdue", type: "overdue" };
    } else if (daysUntil === 0) {
      return { text: "Due today", type: "due" };
    } else if (daysUntil <= 30) {
      return {
        text: `Due in ${daysUntil} day${daysUntil > 1 ? "s" : ""}`,
        type: "due",
      };
    } else {
      const months = Math.floor(daysUntil / 30);
      return {
        text: `Due in ${months} month${months > 1 ? "s" : ""}`,
        type: "future",
      };
    }
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (date) => {
    return dayjs(parseInt(date)).format("MMM D, YYYY");
  };

  const columns = [
    {
      title: <span className="table-header-text">Payment #</span>,
      dataIndex: "order",
      key: "order",
      render: (order) => (
        <span className="payment-number">Payment #{order}</span>
      ),
    },
    {
      title: <span className="table-header-text">Status</span>,
      dataIndex: "status",
      key: "status",
      render: (_, record) => {
        const status = getStatusDisplay(record);
        return (
          <span className={`payment-status ${status.type}`}>{status.text}</span>
        );
      },
    },
    {
      title: <span className="table-header-text">Date</span>,
      dataIndex: "next_attempt",
      key: "next_attempt",
      render: (date) => (
        <span className="payment-date">{formatDate(date)}</span>
      ),
    },
    {
      title: <span className="table-header-text">Amount</span>,
      dataIndex: "amount",
      key: "amount",
      render: (amount) => (
        <span className="payment-amount">{formatAmount(amount)}</span>
      ),
    },
  ];

  const paidCount = payments.filter(
    (p) => p.status === "paid" || p.status === "success"
  ).length;

  if (loading) {
    return (
      <div className="payment-schedule-panel loading">
        <div className="panel-loading">
          <div className="loading-spinner" />
        </div>
      </div>
    );
  }

  const displayedPayments = showAll ? payments : payments.slice(0, 5);

  return (
    <div className="payment-schedule-panel">
      <div className="panel-header">
        <div className="header-content">
          <div className="title-section">
            <h2 className="panel-title">Payment Schedule</h2>
            <p className="panel-subtitle">
              {paidCount} of {payments.length} Paid
            </p>
          </div>
          {bankAccount && (
            <div className="payment-method">
              <div className="payment-method-icon">
                {bankAccount.logo ? (
                  <img
                    src={`data:image/png;base64, ${bankAccount.logo}`}
                    alt={bankAccount.name}
                  />
                ) : bankAccount.account_type === "creditcard" ? (
                  <IconCreditCardFilled size={18} />
                ) : (
                  <IconBuildingBank size={18} />
                )}
              </div>
              <span className="payment-method-text">
                {isMobile
                  ? `****${bankAccount.last_four}`
                  : `${bankAccount.name} •••• ${bankAccount.last_four}`}
              </span>
            </div>
          )}
        </div>
      </div>
      {isMobile ? (
        <div className="mobile-payment-list">
          {displayedPayments.map((payment, index) => {
            const status = getStatusDisplay(payment);
            const isLast = index === displayedPayments.length - 1;
            return (
              <div
                key={payment.id}
                className={`mobile-payment-item ${isLast ? "last" : ""}`}
              >
                <div className="mobile-payment-row">
                  <span className="mobile-payment-number">
                    Payment #{payment.order}
                  </span>
                  <span className="mobile-payment-amount">
                    {formatAmount(payment.amount)}
                  </span>
                </div>
                <div className="mobile-payment-meta">
                  <span className={`payment-status ${status.type}`}>
                    {status.type === "paid" && <IconCheck size={12} />}
                    {status.text}
                  </span>
                  <span className="mobile-payment-date">
                    {formatDate(payment.next_attempt)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="table-container">
          <Table
            columns={columns}
            dataSource={displayedPayments}
            rowKey="id"
            pagination={false}
            showHeader={true}
            size="small"
          />
        </div>
      )}
      {payments.length > 5 && (
        <div className="panel-footer">
          <button
            className="see-full-schedule-button"
            onClick={() => setShowAll(!showAll)}
          >
            <span>{showAll ? "Hide Schedule" : "See Full Schedule"}</span>
            <IconChevronDown
              size={16}
              className={`chevron-icon ${showAll ? "rotated" : ""}`}
            />
          </button>
        </div>
      )}
    </div>
  );
}

export default PaymentSchedulePanel;
